import React, { useState, useEffect } from 'react';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';

import FormInputText from '../../../components/common/stories/FormInputText';
import FormButton from '../../../components/common/stories/FormButton';
import { useNotification } from '../../../context/NotificationContext';
import { storeWebsiteApi } from '../services/storeWebsiteApi';

const DomainConfiguration = ({ website, onUpdate, isSaving, platformDomain }) => {
  const notification = useNotification();
  const [subdomainSlug, setSubdomainSlug] = useState(website?.subdomain_slug || '');
  const [orgSlug, setOrgSlug] = useState(website?.org_slug || '');
  const [customDomain, setCustomDomain] = useState(website?.custom_domain || '');
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    setSubdomainSlug(website?.subdomain_slug || '');
    setOrgSlug(website?.org_slug || '');
    setCustomDomain(website?.custom_domain || '');
  }, [website]);

  const handleSaveSubdomain = async () => {
    if (!subdomainSlug.trim() || !orgSlug.trim()) {
      notification.error('Both subdomain and organization slug are required');
      return;
    }

    await onUpdate({
      subdomain_slug: storeWebsiteApi.generateSlug(subdomainSlug),
      org_slug: storeWebsiteApi.generateSlug(orgSlug),
    });
  };

  const handleSaveCustomDomain = async () => {
    await onUpdate({
      custom_domain: customDomain.toLowerCase().trim(),
      custom_domain_verified: false,
    });
  };

  const handleVerifyDomain = async () => {
    try {
      setIsVerifying(true);
      const result = await storeWebsiteApi.verifyDomain(website._id);
      if (result.verified) {
        notification.success('Domain verified successfully!');
      } else {
        notification.warning('Domain not yet verified. Please check your DNS settings.');
      }
    } catch (err) {
      notification.error('Failed to verify domain');
    } finally {
      setIsVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    notification.success('Copied to clipboard');
  };

  const cnameTarget = `sites.${platformDomain}`;

  return (
    <div className="settings-form">
      {/* Subdomain Configuration */}
      <div className="domain-section">
        <h4>Platform Subdomain</h4>
        <p className="helper-text">
          Your website will be accessible at this URL. You can customize the subdomain and organization parts.
        </p>

        <div className="domain-preview">
          <span className="static">https://</span>
          <input
            type="text"
            className="editable"
            value={subdomainSlug}
            onChange={(e) => setSubdomainSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="location"
            style={{ width: 100 }}
          />
          <span className="static">-</span>
          <input
            type="text"
            className="editable"
            value={orgSlug}
            onChange={(e) => setOrgSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="organization"
            style={{ width: 150 }}
          />
          <span className="static">.{platformDomain}</span>
        </div>

        <div style={{ marginTop: 15 }}>
          <FormButton onClick={handleSaveSubdomain} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Subdomain'}
          </FormButton>
        </div>
      </div>

      {/* Custom Domain Configuration */}
      <div className="domain-section">
        <h4>Custom Domain (Optional)</h4>
        <p className="helper-text">
          Connect your own domain to your store website. For example: www.emangrocerys.ca
        </p>

        <div className="form-row">
          <FormInputText
            label=""
            placeholder="www.yourdomain.com"
            value={customDomain}
            onChange={(e) => setCustomDomain(e.target.value)}
            width="60%"
          />
          <FormButton onClick={handleSaveCustomDomain} disabled={isSaving || !customDomain}>
            {isSaving ? 'Saving...' : 'Set Custom Domain'}
          </FormButton>
        </div>

        {website?.custom_domain && (
          <>
            <div className={`verification-status ${website.custom_domain_verified ? 'verified' : 'pending'}`}>
              {website.custom_domain_verified ? (
                <>
                  <CheckCircleIcon /> Domain verified and active
                </>
              ) : (
                <>
                  <WarningIcon /> Domain pending verification
                </>
              )}
            </div>

            {!website.custom_domain_verified && (
              <div className="dns-instructions">
                <h5>DNS Configuration Required</h5>
                <p>Add the following CNAME record to your domain's DNS settings:</p>
                <div className="dns-record">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong>Type:</strong> CNAME<br />
                      <strong>Name:</strong> {customDomain.startsWith('www.') ? 'www' : '@'}<br />
                      <strong>Target:</strong> {cnameTarget}
                    </div>
                    <Tooltip title="Copy CNAME target">
                      <IconButton onClick={() => copyToClipboard(cnameTarget)} size="small">
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                <p style={{ fontSize: 13, color: '#666', marginBottom: 15 }}>
                  DNS changes can take up to 48 hours to propagate, but usually complete within a few minutes.
                </p>
                <FormButton onClick={handleVerifyDomain} disabled={isVerifying}>
                  {isVerifying ? 'Verifying...' : 'Verify Domain'}
                </FormButton>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DomainConfiguration;
